'use client';

import { useState } from 'react';
import type { InternalNetworkDto } from '@terra/shared-contracts-internal-network';
import { automationId } from '@terra/shared-utils';
import {
  Field,
  Input,
  SettingsBooleanControl,
  SettingsCard,
  SettingsEditDialog,
  SettingsRow,
  SettingsSection,
  toasterApi,
} from '@terra/ui-design-system';
import { VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { useUpdateInternalNetwork } from '../../../../hooks/api/useSurfaceSettings';

type InternalGeneralSettingsPanelProps = {
  surface: InternalNetworkDto;
  networkId: string;
  isReadOnly: boolean;
};

export function InternalGeneralSettingsPanel({
  surface,
  networkId,
  isReadOnly,
}: InternalGeneralSettingsPanelProps) {
  const t = useTranslations('internalNetwork.settings');
  const tCommon = useTranslations('common');
  const updateSurface = useUpdateInternalNetwork(networkId);
  const [isNameDialogOpen, setIsNameDialogOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState(surface.name);
  const handleEnabledChange = async (checked: boolean) => {
    try {
      await updateSurface.mutateAsync({ isEnabled: checked });
      toasterApi.success({ title: t('saveSuccess') });
    } catch (error: unknown) {
      console.warn('Failed to update internal network surface enabled state', {
        error: error instanceof Error ? error : new Error(String(error)),
      });
      toasterApi.error({ title: t('saveError') });
    }
  };

  const handleSaveName = async () => {
    const trimmedName = nameDraft.trim();

    if (!trimmedName) {
      return;
    }

    try {
      await updateSurface.mutateAsync({ name: trimmedName });
      toasterApi.success({ title: t('saveSuccess') });
      setIsNameDialogOpen(false);
    } catch (error: unknown) {
      console.warn('Failed to update internal network surface name', {
        error: error instanceof Error ? error : new Error(String(error)),
      });
      toasterApi.error({ title: t('saveError') });
    }
  };

  return (
    <VStack alignItems="stretch" gap={8} w="full">
      <SettingsSection title={t('general.metadata')}>
        <SettingsCard>
          <SettingsRow
            label={t('general.name')}
            value={surface.name}
            valueTestId="internal-network-settings-name-value"
            action={{
              label: tCommon('edit'),
              onClick: () => {
                setNameDraft(surface.name);
                setIsNameDialogOpen(true);
              },
              testId: 'internal-network-settings-name-edit',
            }}
            isReadOnly={isReadOnly}
          />
          <SettingsRow
            label={t('general.enabled')}
            control={
              <SettingsBooleanControl
                checked={surface.isEnabled}
                disabled={updateSurface.isPending || isReadOnly}
                label={
                  surface.isEnabled ? tCommon('enabled') : tCommon('disabled')
                }
                onCheckedChange={({ checked }) =>
                  void handleEnabledChange(checked)
                }
              />
            }
          />
        </SettingsCard>
      </SettingsSection>

      <SettingsEditDialog
        open={isNameDialogOpen}
        title={t('general.editNameTitle')}
        onOpenChange={setIsNameDialogOpen}
        onSubmit={() => void handleSaveName()}
        loading={updateSurface.isPending}
        isSubmitDisabled={!nameDraft.trim()}
        contentAutomationId="internal-network-settings-name-dialog"
        submitAutomationId="internal-network-settings-name-save"
      >
        <Field.Root>
          <Field.Label>{t('general.name')}</Field.Label>
          <Input
            value={nameDraft}
            onChange={(event) => setNameDraft(event.target.value)}
            {...automationId('internal-network-settings-name-input')}
          />
        </Field.Root>
      </SettingsEditDialog>
    </VStack>
  );
}
